import { ROOM_PRESSURE_TABLE } from '../data/enemies.data.js';

const MAX_ACTIVE_ENEMIES = 12;
const MIN_PLAYER_START_DISTANCE = 3;
const SPAWN_EDGE_INSET = 1.4;

const state = {
  roomNumber: 0,
  config: null,
  timeElapsed: 0,
  killCount: 0,
  currentSpawnInterval: 0,
  spawnTimer: 0,
  activeEnemies: [],
  isQuotaMet: false,
  isActive: false,
  roomBounds: { minX: -18, maxX: 18, minZ: -18, maxZ: 18 },
  playerStartPos: { x: 0, z: 0 },
  spawnEnemyFn: null,
  onQuotaMet: null,
};

function resetState() {
  state.roomNumber = 0;
  state.config = null;
  state.timeElapsed = 0;
  state.killCount = 0;
  state.currentSpawnInterval = 0;
  state.spawnTimer = 0;
  state.activeEnemies = [];
  state.isQuotaMet = false;
  state.isActive = false;
  state.roomBounds = { minX: -18, maxX: 18, minZ: -18, maxZ: 18 };
  state.playerStartPos = { x: 0, z: 0 };
  state.spawnEnemyFn = null;
  state.onQuotaMet = null;
}

function getAllowedTypes(escalation = [], elapsedSeconds = 0) {
  let types = ['drone'];
  for (const entry of escalation) {
    if (!entry || !Array.isArray(entry.allowedTypes)) continue;
    if ((Number(entry.afterSeconds) || 0) <= elapsedSeconds) {
      types = entry.allowedTypes;
    }
  }
  return types.length > 0 ? types : ['drone'];
}

function pickSpawnPositionOnEdge() {
  const bounds = state.roomBounds || {};
  const rawMinX = Number(bounds.minX) || -18;
  const rawMaxX = Number(bounds.maxX) || 18;
  const rawMinZ = Number(bounds.minZ) || -18;
  const rawMaxZ = Number(bounds.maxZ) || 18;
  const minX = Math.min(rawMinX + SPAWN_EDGE_INSET, rawMaxX - 0.5);
  const maxX = Math.max(rawMaxX - SPAWN_EDGE_INSET, rawMinX + 0.5);
  const minZ = Math.min(rawMinZ + SPAWN_EDGE_INSET, rawMaxZ - 0.5);
  const maxZ = Math.max(rawMaxZ - SPAWN_EDGE_INSET, rawMinZ + 0.5);
  const px = Number(state.playerStartPos?.x) || 0;
  const pz = Number(state.playerStartPos?.z) || 0;
  const width = Math.max(0.001, maxX - minX);
  const depth = Math.max(0.001, maxZ - minZ);

  for (let i = 0; i < 40; i += 1) {
    const edge = Math.floor(Math.random() * 4);
    let x = minX;
    let z = minZ;
    if (edge === 0) {
      x = minX + Math.random() * width;
      z = minZ;
    } else if (edge === 1) {
      x = minX + Math.random() * width;
      z = maxZ;
    } else if (edge === 2) {
      x = minX;
      z = minZ + Math.random() * depth;
    } else {
      x = maxX;
      z = minZ + Math.random() * depth;
    }
    if (Math.hypot(x - px, z - pz) >= MIN_PLAYER_START_DISTANCE) {
      return { x, z };
    }
  }

  const corners = [
    { x: minX, z: minZ },
    { x: maxX, z: minZ },
    { x: minX, z: maxZ },
    { x: maxX, z: maxZ },
  ];
  corners.sort((a, b) => Math.hypot(b.x - px, b.z - pz) - Math.hypot(a.x - px, a.z - pz));
  return corners[0] || { x: minX, z: minZ };
}

function _spawnEnemy({ excludeTypes = [] } = {}) {
  if (!state.isActive || typeof state.spawnEnemyFn !== 'function') return;
  if (state.activeEnemies.length >= MAX_ACTIVE_ENEMIES) return;
  const allowedTypes = getAllowedTypes(state.config?.escalation, state.timeElapsed);
  const blockedTypes = [...excludeTypes];
  if (state.roomNumber >= 2) blockedTypes.push('drone');
  const filteredAllowedTypes = allowedTypes.filter((enemyType) => !blockedTypes.includes(enemyType));
  const pool = filteredAllowedTypes.length > 0 ? filteredAllowedTypes : allowedTypes;
  const type = pool[Math.floor(Math.random() * pool.length)] || 'drone';
  const position = pickSpawnPositionOnEdge();
  const enemyInstance = state.spawnEnemyFn(type, position);
  if (enemyInstance) {
    state.activeEnemies.push(enemyInstance);
  }
  return enemyInstance;
}

export const spawnRuntime = {
  initRoom(roomNumber, roomBounds, playerStartPos, spawnEnemyFn, onQuotaMet) {
    resetState();
    state.roomNumber = Number(roomNumber) || 0;
    state.config = ROOM_PRESSURE_TABLE[state.roomNumber] || null;
    state.roomBounds = roomBounds || state.roomBounds;
    state.playerStartPos = {
      x: Number(playerStartPos?.x) || 0,
      z: Number(playerStartPos?.z) || 0,
    };
    state.spawnEnemyFn = typeof spawnEnemyFn === 'function' ? spawnEnemyFn : null;
    state.onQuotaMet = typeof onQuotaMet === 'function' ? onQuotaMet : null;

    if (!state.config || state.config.isBoss) {
      state.isActive = false;
      return;
    }

    state.isActive = true;
    state.currentSpawnInterval = Number(state.config.spawnIntervalStart) || 1.5;
    state.spawnTimer = state.currentSpawnInterval;
    const firstSpawn = _spawnEnemy({ excludeTypes: ['minmon'] });
    const secondSpawn = _spawnEnemy({ excludeTypes: ['minmon'] });
    const initialRoomSpawnNoShootSeconds = 3.0;
    if (firstSpawn) firstSpawn.fireCd = Math.max(Number(firstSpawn.fireCd) || 0, initialRoomSpawnNoShootSeconds);
    if (secondSpawn) secondSpawn.fireCd = Math.max(Number(secondSpawn.fireCd) || 0, initialRoomSpawnNoShootSeconds);
  },

  update(delta) {
    if (!state.isActive) return;
    const dt = Math.max(0, Number(delta) || 0);
    state.timeElapsed += dt;
    state.spawnTimer -= dt;
    state.activeEnemies = state.activeEnemies.filter((enemy) => enemy?.alive);
    if (state.spawnTimer <= 0) {
      if (state.activeEnemies.length < MAX_ACTIVE_ENEMIES) {
        _spawnEnemy();
        _spawnEnemy();
      }
      state.spawnTimer = state.currentSpawnInterval;
    }
  },

  onEnemyKilled(enemyInstance) {
    state.activeEnemies = state.activeEnemies.filter((enemy) => enemy && enemy !== enemyInstance && enemy.alive);
    if (!state.isActive || !state.config || state.config.isBoss) return;
    state.killCount += 1;
    const nextInterval = state.currentSpawnInterval - (Number(state.config.intervalDecayRate) || 0);
    state.currentSpawnInterval = Math.max(Number(state.config.spawnIntervalMin) || 0.6, nextInterval);
    if (state.killCount >= (Number(state.config.killQuota) || 0) && !state.isQuotaMet) {
      state.isQuotaMet = true;
      state.onQuotaMet?.();
    }
  },

  getKillCount() {
    return state.killCount;
  },

  getQuota() {
    return Number(state.config?.killQuota) || 0;
  },

  isRoomComplete() {
    return state.isQuotaMet;
  },

  getActiveEnemies() {
    return state.activeEnemies.filter((enemy) => enemy?.alive);
  },

  stop() {
    resetState();
  },
};
